// Öffentliche (EE-kuratierte) Playlists — CMS-Verwaltung (Phase 2).
// Liste + Create/Edit-Form (gleiches showForm-Muster wie AudioManagement). Owner = EE-System,
// Authz macht das Backend; hier ist die Kuratier-Oberfläche.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { PlusIcon, PhotoIcon, QueueListIcon } from '@heroicons/react/24/outline';
import { playlistService } from '../services/playlistService';
import { PlaylistForm } from '../components/forms/PlaylistForm';
import { PageLayout } from '../components/ui/PageLayout';
import { Playlist, PlaylistStatus } from '../types/playlist';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Benachrichtigung } from '../components/ui/Benachrichtigung';
import { Filterleiste } from '../components/ui/Filterleiste';
import { SeitenblaetterFuss } from '../components/ui/SeitenblaetterFuss';
import { kuratierStatusAuswahl, seitenwechsel } from '../components/ui/filterZustand';
import { useBenachrichtigung } from '../hooks/useBenachrichtigung';
import { useSeitenausschnitt } from '../hooks/useSeitenausschnitt';

/** Zeilen je Seite — dieselbe Begründung wie in `AlbumManagement`: reine Anzeige-Größe. */
const ZEILEN_JE_SEITE = 20;

/**
 * Filter-Zustand dieser Seite; `page` liegt aus demselben Grund mit drin wie bei den Alben.
 *
 * ⚠️ Was hier bewusst NICHT steht: `isPublic` und `collaborative`. Beide Felder gibt es am
 * Datensatz, aber diese Seite zeigt ausschließlich die EE-eigenen Playlists — der Dienst scoped
 * jede Abfrage auf `EE_SYSTEM_USER_ID`. Ein Filter, dessen eine Hälfte nie ein Ergebnis hat, ist
 * eine Zusage, die die Liste nicht halten kann.
 */
interface PlaylistFilter {
  page: number;
  suche: string;
  status: PlaylistStatus | undefined;
}

const PlaylistManagement: React.FC = () => {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Playlist | null>(null);
  const [filter, setFilter] = useState<PlaylistFilter>({ page: 1, suche: '', status: undefined });
  // `melde` trägt exakt die Signatur, die `PlaylistForm` als `notify` erwartet — das Formular
  // bleibt deshalb unangetastet. Der frühere Handbau setzte seinen `setTimeout` ohne
  // `clearTimeout` und räumte auch Fehler nach vier Sekunden weg; beides liegt jetzt im Haken.
  const { inhalt, melde, schließe } = useBenachrichtigung();

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setPlaylists(await playlistService.getPlaylists());
    } catch {
      melde('error', 'Playlists konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [melde]);

  useEffect(() => {
    void load();
  }, [load]);

  /** `page: 1` steht HINTER dem gesetzten Wert — Begründung bei `seitenwechsel`. */
  const setzeFilter = <K extends keyof PlaylistFilter>(feld: K, wert: PlaylistFilter[K]) => {
    setFilter((vorher) => ({ ...vorher, [feld]: wert, page: 1 }));
  };

  /**
   * Gesucht wird über den Namen — die einzige Textspalte dieser Tabelle.
   *
   * ⚠️ Die Ersteller-Marke (`artist`) bleibt draußen, obwohl sie am Datensatz hängt: sie steht in
   * dieser Liste in keiner Spalte. Ein Treffer über sie zeigte eine Zeile, der man nicht ansieht,
   * warum sie gefunden wurde. Dieselbe Regel wie bei der Kurzbiografie in `ArtistManagement`.
   *
   * ⚠️ Keine Entprellung — es geht keine Anfrage raus (siehe `useSeitenausschnitt`).
   */
  const gefiltert = useMemo(() => {
    const suche = filter.suche.trim().toLowerCase();
    return playlists.filter((pl) => {
      if (filter.status && pl.status !== filter.status) return false;
      if (!suche) return true;
      return pl.name.toLowerCase().includes(suche);
    });
  }, [playlists, filter.suche, filter.status]);

  const { ausschnitt, seite, seitenAnzahl, gesamt } = useSeitenausschnitt(
    gefiltert,
    ZEILEN_JE_SEITE,
    filter,
    setFilter,
  );

  const handleCreate = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleEdit = async (pl: Playlist) => {
    try {
      const full = await playlistService.getPlaylistById(pl.id);
      setEditing(full);
    } catch {
      setEditing(pl); // Fallback: Listen-Objekt enthält bereits items
    }
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditing(null);
    void load();
  };

  const handleDeleted = () => {
    setShowForm(false);
    setEditing(null);
    melde('success', 'Playlist gelöscht');
    void load();
  };

  if (showForm) {
    return (
      <PageLayout title={editing ? 'Playlist bearbeiten' : 'Neue öffentliche Playlist'}>
        {/* Zweiter Aufrufort desselben Bausteins: der Formular-Zweig. Vorher standen hier und im
            Listen-Zweig zwei handgleiche Kopien — wer eine anfasste, sah die andere nicht.

            ⚠️ NUR HIER `schwebend`, nicht im Listen-Zweig unten — der Unterschied ist belegt, nicht
            Geschmack: `PlaylistForm` öffnet den `TrackPicker`, ein Overlay über die ganze Fläche
            (`fixed inset-0 z-50 bg-black/50`). Sein `onAdd` läuft in `PlaylistForm.handleAddTrack`,
            und dessen `catch` meldet „Track konnte nicht hinzugefügt werden" über genau dieses
            `notify` — ohne den Picker zu schließen. Im Fluss stünde die Meldung hinter dem
            abgedunkelten Overlay: die Nutzerin klickt einen Track an, er erscheint nicht in der
            Liste, und sichtbar passiert nichts.
            Der Listen-Zweig hat kein Overlay und bleibt deshalb bei der Mehrheitsfassung. */}
        <Benachrichtigung inhalt={inhalt} onSchließen={schließe} schwebend />
        <PlaylistForm
          key={editing?.id || 'new-playlist'}
          initial={editing}
          onCreated={(created) => setEditing(created)}
          onClose={handleClose}
          onDeleted={handleDeleted}
          notify={melde}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Öffentliche Playlists"
      subtitle="Von Energetic Eternity kuratiert."
      actions={
        <button onClick={handleCreate} className="btn btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          Neue Playlist
        </button>
      }
    >
      <Benachrichtigung inhalt={inhalt} onSchließen={schließe} />

      {/* An `playlists.length`, nicht an `gefiltert.length` — sonst nähme der letzte Treffer das
          Suchfeld mit, in dem der zu enge Begriff steht (Herleitung in `AlbumManagement`). */}
      {playlists.length > 0 && (
        <Filterleiste
          suche={filter.suche}
          onSuche={(wert) => setzeFilter('suche', wert)}
          platzhalter="Name..."
          auswahlen={[
            kuratierStatusAuswahl(filter.status, (wert) => setzeFilter('status', wert)),
          ]}
        />
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <div className="loading-spinner mx-auto w-8 h-8"></div>
          <p className="mt-4 text-secondary-600">Lade Playlists…</p>
        </div>
      ) : playlists.length === 0 ? (
        <div className="text-center py-12 card">
          <QueueListIcon className="mx-auto h-12 w-12 text-secondary-400" />
          <p className="mt-4 text-secondary-600">Noch keine öffentlichen Playlists</p>
          <button onClick={handleCreate} className="mt-4 btn btn-primary">
            <PlusIcon className="w-5 h-5 mr-2" />
            Erste Playlist erstellen
          </button>
        </div>
      ) : gefiltert.length === 0 ? (
        /* Zweiter leerer Zustand: „nichts gefunden" ist nicht „nichts vorhanden" — deshalb ohne
           den Anlegen-Knopf des Zweigs darüber. */
        <div className="text-center py-12 card">
          <QueueListIcon className="mx-auto h-12 w-12 text-secondary-400" />
          <p className="mt-4 text-secondary-600">Keine Playlists gefunden</p>
          <p className="mt-1 text-sm text-secondary-500">Suche oder Status anpassen.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          {/* Kopf- und Zellenmaße kommen aus `.table` (index.css) statt aus handkopierten
              Klassenketten. Was dabei bewusst aufgegeben wurde, steht dort am Regelblock. */}
          <table className="table">
            <thead>
              <tr>
                <th>Cover</th>
                <th>Name</th>
                <th>Tracks</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {ausschnitt.map((pl) => (
                <tr key={pl.id}>
                  <td>
                    <button type="button" onClick={() => void handleEdit(pl)} title="Bearbeiten" className="focus:outline-none">
                      {pl.coverImageUrl ? (
                        <img src={pl.coverImageUrl} alt={pl.name} className="h-10 w-10 rounded object-cover" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-secondary-100 flex items-center justify-center">
                          <PhotoIcon className="w-5 h-5 text-secondary-400" />
                        </div>
                      )}
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => void handleEdit(pl)}
                      className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline text-left focus:outline-none"
                    >
                      {pl.name}
                    </button>
                  </td>
                  <td>{pl.itemCount}</td>
                  <td><StatusBadge status={pl.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* `gesamt` zählt die Treffer, nicht alle Playlists — sonst widerspräche die Zahl der
              Tabelle darüber, sobald ein Filter steht. */}
          <SeitenblaetterFuss
            seite={seite}
            seitenAnzahl={seitenAnzahl}
            gesamt={gesamt}
            onSeitenwechsel={seitenwechsel(setFilter)}
          />
        </div>
      )}
    </PageLayout>
  );
};

export default PlaylistManagement;
